import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Copy, Ban, Plus, Link2, RefreshCw } from "lucide-react";

export default function AdminActivations() {
  const [invites, setInvites] = useState([]);
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", access_type: "free_forever", product_id: "", expires_days: 7, trial_months: 1 });
  const [generated, setGenerated] = useState(null);

  const load = () => api.get("/admin/invites", { params: status ? { status } : {} }).then((r) => setInvites(r.data));
  useEffect(() => { load(); api.get("/admin/products").then((r) => setProducts(r.data)); }, [status]);

  const generate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/invites/generate", {
        email: form.email,
        access_type: form.access_type,
        product_id: form.access_type === "legacy" ? (form.product_id || null) : null,
        trial_months: form.access_type === "free_trial" ? Math.max(1, Math.min(24, +form.trial_months || 1)) : null,
        expires_days: form.expires_days, cancel_previous: true,
      });
      setGenerated(data);
      toast.success("تم إنشاء الرابط");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const cancel = async (id) => {
    if (!window.confirm("إلغاء الرابط؟")) return;
    await api.post(`/admin/invites/${id}/cancel`);
    toast.success("تم الإلغاء");
    load();
  };

  const copyLink = async (link) => { await navigator.clipboard.writeText(link); toast.success("تم النسخ"); };

  return (
    <div className="space-y-4" data-testid="admin-invites">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-display font-semibold">روابط التفعيل</h1>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setGenerated(null); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 rounded-full" data-testid="btn-open-generate"><Plus className="h-4 w-4" /> إنشاء رابط</Button>
          </DialogTrigger>
          <DialogContent dir="rtl">
            <DialogHeader><DialogTitle>إنشاء رابط تفعيل جديد</DialogTitle></DialogHeader>
            {generated ? (
              <div className="space-y-3">
                <Label>الرابط:</Label>
                <div className="p-3 bg-secondary rounded-lg text-xs break-all font-mono" data-testid="generated-link">{generated.activation_link}</div>
                <Button className="w-full rounded-full gap-2" onClick={() => copyLink(generated.activation_link)} data-testid="btn-copy-generated"><Copy className="h-4 w-4" /> نسخ</Button>
                <Button variant="outline" className="w-full" onClick={() => { setGenerated(null); setForm({ email: "", access_type: "free_forever", product_id: "", expires_days: 7 }); }}>إنشاء آخر</Button>
              </div>
            ) : (
              <form onSubmit={generate} className="space-y-3">
                <div className="space-y-2">
                  <Label>البريد الإلكتروني</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
                </div>
                <div className="space-y-2">
                  <Label>نوع الوصول</Label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { v: "free_forever", label: "وصول كامل (مجاني للأبد)", hint: "يمنح كل الكورسات المنشورة + المستقبلية" },
                      { v: "free_trial", label: "تجربة مجانية محددة بالأشهر", hint: "وصول كامل يُقفل تلقائيًا بعد عدد الأشهر" },
                      { v: "legacy", label: "دورة/دورات محددة", hint: "للاستخدام الاستثنائي فقط" },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setForm({ ...form, access_type: opt.v })}
                        className={`px-3 py-1.5 rounded-full text-xs border transition ${form.access_type === opt.v ? "bg-black text-white border-foreground" : "bg-white text-foreground border-foreground/20 hover:bg-foreground/5"}`}
                        data-testid={`invite-access-${opt.v}`}
                        title={opt.hint}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-foreground/50">
                    {form.access_type === "free_forever"
                      ? "الوصول لجميع الكورسات المنشورة الآن + أي كورس مستقبلي (يُضاف تلقائيًا)."
                      : form.access_type === "free_trial"
                        ? "وصول كامل لجميع الكورسات لمدة محددة. بعد انتهاء الفترة، يُقفل الوصول تلقائيًا ويُطلب من المستخدم الدفع."
                        : "يمنح فقط الدورات المرتبطة بالمنتج المختار."}
                  </p>
                </div>
                {form.access_type === "free_trial" && (
                  <div className="space-y-2">
                    <Label>عدد الأشهر المجانية</Label>
                    <div className="flex gap-2 flex-wrap">
                      {[1, 2, 3, 6, 12].map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setForm({ ...form, trial_months: m })}
                          className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${form.trial_months === m ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-foreground border-foreground/20 hover:bg-emerald-50"}`}
                          data-testid={`invite-trial-${m}`}
                        >
                          {m === 1 ? "شهر" : m === 2 ? "شهران" : `${m} أشهر`}
                        </button>
                      ))}
                      <Input
                        type="number"
                        min={1}
                        max={24}
                        value={form.trial_months}
                        onChange={(e) => setForm({ ...form, trial_months: +e.target.value || 1 })}
                        className="w-24 h-9"
                        placeholder="أخرى"
                        data-testid="invite-trial-custom"
                      />
                    </div>
                    <p className="text-[10px] text-emerald-700/80">
                      ⏱ ستنتهي صلاحية الوصول بعد {form.trial_months} {form.trial_months === 1 ? "شهر" : form.trial_months === 2 ? "شهرين" : "أشهر"} من لحظة تفعيل الحساب.
                    </p>
                  </div>
                )}
                {form.access_type === "legacy" && (
                  <div className="space-y-2">
                    <Label>المنتج (يمنح الدورات المرتبطة)</Label>
                    <Select value={form.product_id || "none"} onValueChange={(v) => setForm({ ...form, product_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="invite-product"><SelectValue placeholder="اختر منتجًا" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— بدون منتج —</SelectItem>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>صلاحية الرابط (أيام)</Label>
                  <Input type="number" min={1} max={90} value={form.expires_days} onChange={(e) => setForm({ ...form, expires_days: +e.target.value })} data-testid="invite-days" />
                </div>
                <Button type="submit" className="w-full rounded-full" data-testid="btn-generate-invite">إنشاء</Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2">
        {[["", "الكل"], ["pending", "معلقة"], ["used", "مستخدمة"], ["expired", "منتهية"], ["cancelled", "ملغاة"]].map(([v, l]) => (
          <button key={v || "all"} onClick={() => setStatus(v)} className={`px-3 py-1.5 rounded-full text-xs ${status === v ? "bg-black text-white" : "bg-white"}`} data-testid={`filter-status-${v || "all"}`}>
            {l}
          </button>
        ))}
      </div>

      <Card className="bg-white border-foreground/10">
        <Table>
          <TableHeader>
            <TableRow className="border-foreground/10 hover:bg-transparent">
              <TableHead className="text-foreground">البريد</TableHead>
              <TableHead className="text-foreground">المنتج</TableHead>
              <TableHead className="text-foreground">الحالة</TableHead>
              <TableHead className="text-foreground">المصدر</TableHead>
              <TableHead className="text-foreground">تنتهي</TableHead>
              <TableHead className="text-foreground"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.map((i) => (
              <TableRow key={i.id} className="border-foreground/10" data-testid={`invite-row-${i.id}`}>
                <TableCell className="text-xs">{i.email}</TableCell>
                <TableCell className="text-xs">{i.product_snapshot?.name || "—"}</TableCell>
                <TableCell><Badge variant={i.status === "pending" ? "secondary" : "outline"}>{i.status_ar || i.status}</Badge></TableCell>
                <TableCell className="text-xs">{i.source}</TableCell>
                <TableCell className="text-xs">{i.expires_at?.slice(0, 10)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {i.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="نسخ رابط تفعيل جديد (لا يُرسل بريدًا)"
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/admin/invites/${i.id}/regenerate-link`);
                              await navigator.clipboard.writeText(data.activation_link);
                              toast.success("تم نسخ رابط التفعيل الجديد. صالح لمدة 7 أيام. لصقه لمشاركته مع العميل.");
                              load();
                            } catch (err) {
                              toast.error(extractError(err));
                            }
                          }}
                          data-testid={`btn-copy-link-${i.id}`}
                        >
                          <Link2 className="h-4 w-4 text-primary" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="إعادة إرسال بريد التفعيل مع رابط جديد"
                          onClick={async () => {
                            try {
                              await api.post(`/admin/invites/${i.id}/resend`);
                              toast.success("تم إعادة إرسال بريد التفعيل مع رابط جديد.");
                              load();
                            } catch (err) {
                              toast.error(extractError(err));
                            }
                          }}
                          data-testid={`btn-resend-invite-${i.id}`}
                        >
                          <RefreshCw className="h-4 w-4 text-primary" />
                        </Button>
                        <Button size="sm" variant="ghost" title="إلغاء الرابط" onClick={() => cancel(i.id)} data-testid={`btn-cancel-invite-${i.id}`}>
                          <Ban className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {invites.length === 0 && <TableRow><TableCell colSpan={6} className="text-center opacity-60 py-8">لا روابط</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
